import {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import * as css from './SeatmapManage.css';
import SeatCanvas from './SeatCanvas';
import EditSeatMenu from './EditSeatMenu';
import AssignTicketPopup from './AssignTicketPopup';
import {SoldSeatPopup, ReservedSeatPopup} from './SeatPopups';
import {getSeatmapManage, setSeatmapSeatTickets, removeSeatmapSeatTicket, setSeatmapSeatState,
    createSeatmapPartner, reserveSeatmapSeats, unreserveSeatmapSeats} from '../../../redux/event/eventActions';
import {bounceMessage} from '../../../layout/userMessages/UserMessage';

// Native seat-map MANAGER for NEW-format events, LEGACY-parity UX:
// studio-style pan/zoom (drag + touch + pinch + slider), legacy selection
// (click toggles, box replaces, Shift adds, sold never selects), the legacy
// slide-in edit menu with the exact legacy actions/terminology, ticket-coloured
// seats, and legacy state icons.
const STATE_LEGEND = [
    {key: 'free', label: 'פנוי', color: '#93c5fd'},
    {key: 'blocked', label: 'חסום', color: '#9ca3af'},
    {key: 'hidden', label: 'מוסתר', color: '#e5e7eb'},
    {key: 'reserved', label: 'משוריין', color: '#f59e0b'},
    {key: 'sold', label: 'נמכר', color: '#111827'}
];

function SeatmapManage({eid}) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [version, setVersion] = useState(0);
    const [busy, setBusy] = useState(false);
    const [highlightTid, setHighlightTid] = useState(null);
    const [hoverArea, setHoverArea] = useState(null);
    // hover-focus from the sidebar אזורים list ONLY — the on-map gush chips
    // must NOT zoom (the map would move under the cursor and oscillate)
    const [focusArea, setFocusArea] = useState(null);
    const [assignAct, setAssignAct] = useState(null);   // null | 'replace' | 'add'
    const [soldPopup, setSoldPopup] = useState(null);   // {seat, at}
    const [reservedPopup, setReservedPopup] = useState(null);
    // right panel: collapsed by default (page-design style); choice persists
    const [sideOpen, setSideOpen] = useState(() => localStorage.getItem('smapManageSideOpen') === '1');
    const toggleSide = () => setSideOpen((o) => {
        localStorage.setItem('smapManageSideOpen', o ? '0' : '1');
        return !o;
    });

    const load = () => getSeatmapManage(eid)
        .then((d) => { setData(d); setErr(null); })
        .catch(() => setErr('load'));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [eid]);

    // deep link from the שיריונים page (הצג שריון במפת הושבה):
    // /tickets/seats?rpid=<smpid>&area=<smaid> — select the partner's seats
    // and zoom to the area, once, when the data first arrives.
    const location = useLocation();
    const deepLinked = useRef(false);
    useEffect(() => {
        if(!data || deepLinked.current) return;
        const params = new URLSearchParams(location.search);
        const rpid = Number(params.get('rpid'));
        const area = Number(params.get('area'));
        if(!rpid && !area) return;
        deepLinked.current = true;
        if(rpid) setSelected(new Set(data.seats.filter((s) => s.smpid === rpid && s.state !== 'sold').map((s) => s.code)));
        if(area) setFocusArea(area);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const seats = data?.seats || [];
    const tickets = useMemo(() => (data?.tickets || []).filter((t) => t.active !== 0), [data]);
    const ticketColors = useMemo(() => {
        const m = new Map();
        for(const t of data?.tickets || []) m.set(t.tid, `#${(t.color || '').replace('#', '') || '93c5fd'}`);
        return m;
    }, [data]);
    const selCodes = () => Array.from(selected);

    const counts = useMemo(() => {
        const t = {}, st = {free: 0, blocked: 0, hidden: 0, reserved: 0, sold: 0}, partner = {};
        for(const s of seats) {
            st[s.state] = (st[s.state] || 0) + 1;
            for(const tid of s.tids || []) t[tid] = (t[tid] || 0) + 1;
            if(s.smpid) partner[s.smpid] = (partner[s.smpid] || 0) + 1;
        }
        return {byTid: t, byState: st, byPartner: partner};
    }, [seats]);

    const selectedSeats = useMemo(() => seats.filter((s) => selected.has(s.code)), [seats, selected]);
    const highlightCodes = useMemo(() => {
        if(hoverArea != null) return seats.filter((s) => s.smaid === hoverArea).map((s) => s.code);
        if(highlightTid != null) return seats.filter((s) => (s.tids || []).includes(highlightTid)).map((s) => s.code);
        return null;
    }, [seats, hoverArea, highlightTid]);

    const refetch = async () => { await load(); setVersion((v) => v + 1); };

    // optimistic local patch — the legacy editor updates instantly; a full
    // 6k-seat refetch takes seconds. UI patches immediately, the server truth
    // resyncs silently in the background.
    const patchSeats = (updater) => {
        setData((d) => ({...d, seats: d.seats.map(updater)}));
        setVersion((v) => v + 1);
    };

    const run = async (fn, okMsg, patch) => {
        if(!selected.size) return bounceMessage('בחרו מושבים קודם', 'attention');
        setBusy(true);
        try {
            await fn();
            if(patch) { patch(); load().then(() => setVersion((v) => v + 1)); }
            else await refetch();
            if(okMsg) bounceMessage(okMsg, 'success');
        } catch (e) {
            const server = e?.response?.data?.errorMessage;
            bounceMessage(server && /last ticket/i.test(String(server))
                ? 'אין אפשרות למחוק כרטיס כיון שלא לכל המושבים יש כרטיס נוסף'
                : 'הפעולה נכשלה', 'attention');
            await refetch();
        }
        finally { setBusy(false); }
    };

    // select every SELECTABLE seat of a gush (sold seats never join — legacy)
    const selectArea = (smaid, additive) => {
        const codes = seats.filter((s) => s.smaid === smaid && s.state !== 'sold').map((s) => s.code);
        setSelected((prev) => {
            const next = additive ? new Set(prev) : new Set();
            codes.forEach((c) => next.add(c));
            return next;
        });
    };
    const selectAll = () => setSelected(new Set(seats.filter((s) => s.state !== 'sold').map((s) => s.code)));
    const clearSelection = () => setSelected(new Set());
    const selectPartnerSeats = (smpid) =>
        setSelected(new Set(seats.filter((s) => s.smpid === smpid && s.state !== 'sold').map((s) => s.code)));

    const partners = data?.partners || [];

    // ---- menu actions (legacy flows; all patch the UI instantly) ----
    const doAssign = (tids) => {
        const act = assignAct;
        setAssignAct(null);
        const codes = new Set(selCodes());
        return run(() => setSeatmapSeatTickets(selCodes(), tids, act),
            act === 'replace' ? 'הכרטיס שויך למושבים' : 'הכרטיס נוסף למושבים',
            () => patchSeats((s) => {
                if(!codes.has(s.code)) return s;
                const next = act === 'replace' ? [...tids] : [...new Set([...(s.tids || []), ...tids])];
                return {...s, tids: next};
            }));
    };
    const doDeleteTicket = (tid) => {
        const codes = new Set(selCodes());
        return run(() => removeSeatmapSeatTicket(selCodes(), tid), 'הכרטיס הוסר מהמושבים',
            () => patchSeats((s) => codes.has(s.code) && (s.tids || []).length > 1
                ? {...s, tids: s.tids.filter((t) => t !== tid)} : s));
    };
    // legacy show_close: blocked-toggle applies to the NON-reserved selection
    const doToggleBlocked = (release) => {
        const codes = selectedSeats.filter((s) => s.state !== 'reserved').map((s) => s.code);
        if(!codes.length) return bounceMessage('בחרו מושבים קודם', 'attention');
        const set = new Set(codes);
        return run(() => setSeatmapSeatState(codes, release ? 'free' : 'blocked'),
            (release ? 'נפתחו ' : 'נסגרו ') + codes.length + ' מושבים. ',
            () => patchSeats((s) => set.has(s.code) && s.state !== 'sold' ? {...s, state: release ? 'free' : 'blocked'} : s));
    };
    const doToggleHidden = (show) => {
        const codes = selectedSeats.filter((s) => s.state !== 'reserved').map((s) => s.code);
        if(!codes.length) return bounceMessage('בחרו מושבים קודם', 'attention');
        const set = new Set(codes);
        return run(() => setSeatmapSeatState(codes, show ? 'free' : 'hidden'),
            show ? 'המושבים מוצגים שוב' : 'המושבים הוסתרו',
            () => patchSeats((s) => set.has(s.code) && s.state !== 'sold' ? {...s, state: show ? 'free' : 'hidden'} : s));
    };
    const doReserve = (smpid) => {
        const set = new Set(selCodes());
        return run(() => reserveSeatmapSeats(selCodes(), smpid),
            'שוריינו ' + selected.size + ' מושבים. ',
            () => patchSeats((s) => set.has(s.code) && s.state !== 'sold' ? {...s, state: 'reserved', smpid} : s));
    };
    const doUnreserve = () => {
        const set = new Set(selCodes());
        return run(() => unreserveSeatmapSeats(selCodes()), 'השריון הוסר',
            () => patchSeats((s) => set.has(s.code) && s.state === 'reserved' ? {...s, state: 'free', smpid: null} : s));
    };
    const doCreatePartner = async (fields) => {
        setBusy(true);
        try {
            const p = await createSeatmapPartner(fields);
            await load();
            bounceMessage('השותף נוצר', 'success');
            return p;
        } catch { bounceMessage('יצירת השותף נכשלה', 'attention'); return null; }
        finally { setBusy(false); }
    };

    if(err) return <css.wrap><css.empty>לא ניתן לטעון את מפת המושבים.</css.empty></css.wrap>;
    if(!data) return <css.wrap><css.empty>טוען מפת מושבים…</css.empty></css.wrap>;
    if(!seats.length) return <css.wrap><css.empty>למפה זו אין מושבים.</css.empty></css.wrap>;

    return (
        <css.wrap>
            <css.body style={{position: 'relative'}}>
                <SeatCanvas seats={seats} bounds={data.bounds} dataVersion={version}
                    selected={selected} setSelected={setSelected}
                    ticketColors={ticketColors} highlightCodes={highlightCodes}
                    areas={data.areas} onSelectArea={selectArea} onHoverArea={setHoverArea}
                    focusArea={focusArea}
                    onSoldClick={(seat, at) => setSoldPopup({seat, at})}
                    onReservedClick={(seat, at) => setReservedPopup({seat, at})} />

                {/* right panel starts COLLAPSED (page-design style); preference sticks */}
                <css.side className={sideOpen ? 'open' : ''}>
                    <css.sideToggle onClick={toggleSide} title={sideOpen ? 'כיווץ הפאנל' : 'פתיחת הפאנל'}>
                        {sideOpen ? '❯' : '❮'}
                    </css.sideToggle>
                    {!sideOpen && <css.sideRail onClick={toggleSide}>פרטים ומקרא</css.sideRail>}
                    {sideOpen && <>
                    <css.sideTitle>{data.title || 'מפת מושבים'} · {seats.length.toLocaleString('he')} מושבים</css.sideTitle>
                    <css.sideSub>כרטיסים</css.sideSub>
                    {!tickets.length && <css.empty>אין כרטיסים פעילים באירוע.</css.empty>}
                    {tickets.map((t) => (
                        <css.ticketRow key={t.tid}
                            className={highlightTid === t.tid ? 'on' : ''}
                            onClick={() => setHighlightTid(highlightTid === t.tid ? null : t.tid)}>
                            <css.dot style={{background: ticketColors.get(t.tid)}} />
                            <span className="title">{t.title}</span>
                            <span className="cnt">{counts.byTid[t.tid] || 0}</span>
                        </css.ticketRow>
                    ))}
                    {!!partners.length &&
                        <>
                            <css.sideSub>שיריונים</css.sideSub>
                            {partners.map((p) => (
                                <css.legendRow key={p.smpid} style={{cursor: 'pointer'}} onClick={() => selectPartnerSeats(p.smpid)}>
                                    <css.dot style={{background: '#f59e0b'}} /><span>{p.title}</span><span className="cnt">{counts.byPartner[p.smpid] || 0}</span>
                                </css.legendRow>
                            ))}
                        </>}
                    <css.sideSub>מקרא</css.sideSub>
                    {STATE_LEGEND.map((l) => (
                        <css.legendRow key={l.key}><css.dot style={{background: l.color}} /><span>{l.label}</span><span className="cnt">{counts.byState[l.key] || 0}</span></css.legendRow>
                    ))}
                    <css.sideSub>אזורים (לחצו לבחירת גוש)</css.sideSub>
                    <css.areas>{(data.areas || []).map((a) => (
                        <div key={a.smaid} className="clickable"
                            onMouseEnter={() => { setHoverArea(a.smaid); setFocusArea(a.smaid); }}
                            onMouseLeave={() => { setHoverArea(null); setFocusArea(null); }}
                            onClick={(e) => selectArea(a.smaid, e.shiftKey)}>{a.title}</div>
                    ))}</css.areas>
                    </>}
                </css.side>

                {/* LEGACY slide-in edit menu — opens whenever seats are selected */}
                <EditSeatMenu show={selected.size > 0}
                    selectedSeats={selectedSeats} seats={seats} areas={data.areas}
                    tickets={tickets} partners={partners} eid={eid}
                    busy={busy}
                    onSelectPartnerSeats={selectPartnerSeats}
                    onClose={clearSelection}
                    onSelectAll={selectAll}
                    onClearSelection={clearSelection}
                    onToggleBlocked={doToggleBlocked}
                    onToggleHidden={doToggleHidden}
                    onOpenAssign={setAssignAct}
                    onReserveToPartner={doReserve}
                    onUnreserve={doUnreserve}
                    onCreatePartner={doCreatePartner}
                    onDeleteTicket={doDeleteTicket} />
            </css.body>

            {assignAct &&
                <AssignTicketPopup act={assignAct} selectedSeats={selectedSeats} tickets={tickets}
                    eid={eid} busy={busy} onSave={doAssign} onClose={() => setAssignAct(null)} />}
            {soldPopup &&
                <SoldSeatPopup seat={soldPopup.seat} at={soldPopup.at} eid={eid} tickets={data.tickets || []}
                    onClose={() => setSoldPopup(null)} />}
            {reservedPopup &&
                <ReservedSeatPopup seat={reservedPopup.seat} at={reservedPopup.at} eid={eid}
                    tickets={data.tickets || []} partners={partners}
                    onClose={() => setReservedPopup(null)} onSelectPartnerSeats={selectPartnerSeats} />}
        </css.wrap>
    );
}

export default SeatmapManage;
